import type { IntentAlignmentMapping } from '../types/intentAlignment';

// Canonical Intent Alignment Map (Initial Content)
// Strictly Advisory - Non-Gating

export const CANONICAL_INTENT_ALIGNMENTS: IntentAlignmentMapping[] = [
  // IC Queues -> Personal Work
  {
    id: 'ALIGN_IC_01',
    queueId: 'Q_IC_01', // My Open Work
    dashboardIntentTags: ['Personal Work', 'Triage'],
    recommendedDashboardIds: [],
    rationale: 'Aligns individual execution queues with personal dashboard views for daily task management.',
    roles: ['Individual Contributor'],
    maturity: 'basic',
    confidence: 'high'
  },
  {
    id: 'ALIGN_IC_02',
    queueId: 'Q_IC_02', // My High-Priority Work
    dashboardIntentTags: ['Personal Work', 'Risk'],
    recommendedDashboardIds: [],
    rationale: 'Surfaces high-priority personal tasks on risk-focused or personal overview dashboards.',
    roles: ['Individual Contributor'],
    maturity: 'basic',
    confidence: 'high'
  },
  {
    id: 'ALIGN_IC_03',
    queueId: 'Q_IC_03', // Work Waiting on Me
    dashboardIntentTags: ['Personal Work', 'Throughput'],
    recommendedDashboardIds: [],
    rationale: 'Connects "waiting on me" items to personal throughput metrics.',
    roles: ['Individual Contributor'],
    maturity: 'basic',
    confidence: 'high'
  },

  // Team Lead Queues -> Operational Health
  {
    id: 'ALIGN_TL_01',
    queueId: 'Q_TC_01', // Team Active Work
    dashboardIntentTags: ['Operational Health', 'Team Throughput'],
    recommendedDashboardIds: [],
    rationale: 'Provides the active work list backing team throughput dashboards.',
    roles: ['Team Lead', 'Member'],
    maturity: 'basic',
    confidence: 'high'
  },
  {
    id: 'ALIGN_TL_02',
    queueId: 'Q_TL_02', // Team SLA Breach Risk
    dashboardIntentTags: ['Operational Health', 'Risk', 'SLA Risk'],
    recommendedDashboardIds: [],
    rationale: 'Links SLA risk queues directly to SLA health monitors.',
    roles: ['Team Lead'],
    maturity: 'basic',
    confidence: 'high'
  },
  {
    id: 'ALIGN_TL_03',
    queueId: 'Q_TL_03', // Unassigned Team Work
    dashboardIntentTags: ['Operational Health', 'Triage'],
    recommendedDashboardIds: [],
    rationale: 'Ensures unassigned work visible on dashboards has a direct resolution queue.',
    roles: ['Team Lead'],
    maturity: 'basic',
    confidence: 'medium'
  },
  {
    id: 'ALIGN_TL_05',
    queueId: 'Q_TL_05', // Work Blocked or Waiting
    dashboardIntentTags: ['Operational Health', 'Blockers'],
    recommendedDashboardIds: [],
    rationale: 'Correlates blocked ticket counts on dashboards with the actionable list.',
    roles: ['Team Lead'],
    maturity: 'advanced',
    confidence: 'high'
  },

  // Incident Queues -> Incident Command
  {
    id: 'ALIGN_INC_01',
    queueId: 'Q_INC_01', // Major Incidents
    dashboardIntentTags: ['Incident Command', 'Major Incident', 'Risk'],
    recommendedDashboardIds: [],
    rationale: 'Critical for connecting Major Incident dashboards to the active firefighting queue.',
    roles: ['Incident Commander'],
    maturity: 'basic',
    confidence: 'high'
  },
  {
    id: 'ALIGN_INC_02',
    queueId: 'Q_INC_02', // SLA Breach Imminent
    dashboardIntentTags: ['Incident Command', 'SLA Risk'],
    recommendedDashboardIds: [],
    rationale: 'Provides drill-down capability for "At Risk" incident metrics.',
    roles: ['Incident Manager'],
    maturity: 'advanced',
    confidence: 'high'
  },
  {
    id: 'ALIGN_INC_03',
    queueId: 'Q_INC_03', // Active Major Incidents
    dashboardIntentTags: ['Incident Command', 'Major Incident'],
    recommendedDashboardIds: [],
    rationale: 'Direct link from MI status boards to the active incident list.',
    roles: ['Incident Commander'],
    maturity: 'basic',
    confidence: 'high'
  },

  // Change Queues -> Governance
  {
    id: 'ALIGN_CHG_01',
    queueId: 'Q_CHG_01', // Pending Change Approvals
    dashboardIntentTags: ['Change Governance', 'Approvals'],
    recommendedDashboardIds: [],
    rationale: 'Allows CAB members to jump from approval metrics directly to the approval queue.',
    roles: ['Approver', 'CAB'],
    maturity: 'basic',
    confidence: 'high'
  },
  {
    id: 'ALIGN_CHG_02',
    queueId: 'Q_CHG_02', // Emergency Changes In Progress
    dashboardIntentTags: ['Change Governance', 'Risk'],
    recommendedDashboardIds: [],
    rationale: 'Highlights high-risk emergency changes on governance dashboards.',
    roles: ['Change Manager'],
    maturity: 'basic',
    confidence: 'high'
  },
  {
    id: 'ALIGN_CHG_03',
    queueId: 'Q_CHG_03', // Failed/Rolled-Back
    dashboardIntentTags: ['Change Governance', 'Risk', 'Quality'],
    recommendedDashboardIds: [],
    rationale: 'Supports "Change Success Rate" metrics with the list of failed changes.',
    roles: ['Change Manager'],
    maturity: 'advanced',
    confidence: 'high'
  },

  // Problem Queues -> Problem Health
  {
    id: 'ALIGN_PRB_01',
    queueId: 'Q_PRB_01', // Open Problems
    dashboardIntentTags: ['Problem Health', 'Backlog'],
    recommendedDashboardIds: [],
    rationale: 'Backs up problem backlog counts with the actual problem list.',
    roles: ['Problem Manager'],
    maturity: 'basic',
    confidence: 'medium'
  },
  {
    id: 'ALIGN_PRB_02',
    queueId: 'Q_PRB_02', // Problems Without RCA
    dashboardIntentTags: ['Problem Health', 'RCA Compliance', 'Risk'],
    recommendedDashboardIds: [],
    rationale: 'Essential drill-down for RCA compliance dashboards.',
    roles: ['Problem Manager'],
    maturity: 'basic',
    confidence: 'high'
  },

  // Manager/Exec -> Snapshot
  {
    id: 'ALIGN_MGR_02',
    queueId: 'Q_MGR_02', // Workload Distribution
    dashboardIntentTags: ['Workload Balance', 'Team Capacity'],
    recommendedDashboardIds: [],
    rationale: 'Allows managers to investigate load imbalances seen on capacity dashboards.',
    roles: ['Manager'],
    maturity: 'basic',
    confidence: 'medium'
  },
  {
    id: 'ALIGN_EXEC_01',
    queueId: 'Q_EXEC_01', // Operational Risk Snapshot
    dashboardIntentTags: ['Executive Snapshot', 'Risk Posture'],
    recommendedDashboardIds: [],
    rationale: 'Provides the raw data backing high-level executive risk summaries.',
    roles: ['Executive'],
    maturity: 'advanced',
    confidence: 'high'
  },

  // Quality
  {
    id: 'ALIGN_QA_01',
    queueId: 'Q_QA_01', // Reopened
    dashboardIntentTags: ['Quality', 'Rework'],
    recommendedDashboardIds: [],
    rationale: 'Directs users from "Reopen Rate" metrics to the specific reopened tickets.',
    roles: ['QA', 'Manager'],
    maturity: 'advanced',
    confidence: 'high'
  }
];
